import { useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { KpiCard } from "@/lovable-demo/components/bits";
import {
  correctiveActionPrediction,
  illustrativeIssuePrediction,
} from "@/lovable-demo/lib/warranty/claims-prediction";
import { fmtCurrency, fmtNumber } from "@/lovable-demo/lib/warranty/calc";
import type { ProblemRecord } from "@/lovable-demo/lib/warranty/types";

export function CorrectiveActionPrediction({
  records,
  asOf,
  growth,
}: {
  records: ProblemRecord[];
  asOf: string;
  growth: number;
}) {
  const [implementation, setImplementation] = useState("2027-01");
  const [reduction, setReduction] = useState(80);
  const [addressable, setAddressable] = useState(50);
  const [lag, setLag] = useState(1);
  const [issue, setIssue] = useState("all");
  const selected = records.filter(
    (r) => r.status !== "closed" && (issue === "all" || r.id === issue),
  );
  const projections = selected.map((record) =>
    correctiveActionPrediction(
      illustrativeIssuePrediction(record, asOf, growth).future,
      implementation,
      reduction / 100,
      addressable / 100,
      lag,
    ),
  );
  const trend = (projections[0] ?? []).map((p, i) => ({
    month: p.month,
    claims: projections.reduce((sum, row) => sum + (row[i]?.claims ?? 0), 0),
    remaining: projections.reduce((sum, row) => sum + (row[i]?.remaining ?? 0), 0),
    cost: projections.reduce((sum, row) => sum + (row[i]?.cost ?? 0), 0),
    withActionCost: projections.reduce((sum, row) => sum + (row[i]?.withActionCost ?? 0), 0),
  }));
  const without = trend.reduce((sum, p) => sum + p.claims, 0);
  const withAction = trend.reduce((sum, p) => sum + p.remaining, 0);
  const postAction = trend
    .filter((p) => p.month >= implementation)
    .reduce((sum, p) => sum + p.remaining, 0);
  return (
    <section className="my-5 border-y border-border py-5" aria-label="Corrective action prediction">
      <h3 className="text-base font-semibold">
        What if we implement a corrective action in January?
      </h3>
      <p className="my-2 text-sm text-warning">
        Illustrative assumptions · no production or vehicle-cohort data used · not verified
        effectiveness
      </p>
      <div className="my-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <label className="text-xs">
          Issue
          <select
            aria-label="Corrective action issue"
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
            className="mt-2 block w-full rounded border border-input bg-elevated p-2"
          >
            <option value="all">All open issues in view</option>
            {records
              .filter((r) => r.status !== "closed")
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.code} · {r.title}
                </option>
              ))}
          </select>
        </label>
        <label className="text-xs">
          Implementation month
          <input
            aria-label="Implementation month"
            type="month"
            value={implementation}
            onChange={(e) => {
              if (e.target.value) setImplementation(e.target.value);
            }}
            className="mt-2 block w-full rounded border border-input bg-elevated p-2"
          />
        </label>
        <label className="text-xs">
          Failure reduction on reached vehicles · {reduction}%
          <input
            aria-label="Assumed failure reduction"
            type="range"
            min="0"
            max="100"
            value={reduction}
            onChange={(e) => setReduction(Number(e.target.value))}
            className="mt-3 w-full accent-primary"
          />
        </label>
        <label className="text-xs">
          Share of future claims reached by action · {addressable}%
          <input
            aria-label="Assumed addressable claims"
            type="range"
            min="0"
            max="100"
            value={addressable}
            onChange={(e) => setAddressable(Number(e.target.value))}
            className="mt-3 w-full accent-primary"
          />
        </label>
        <label className="text-xs">
          Benefit delay (months)
          <input
            aria-label="Benefit delay"
            type="number"
            min="0"
            max="6"
            value={lag}
            onChange={(e) => setLag(Math.min(6, Math.max(0, Number(e.target.value))))}
            className="mt-2 block w-full rounded border border-input bg-elevated p-2"
          />
        </label>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Starting assumptions: January 2027, 80% failure reduction on an addressable 50% of claims,
        one-month delay, then a three-month linear ramp to full benefit. The other 50% represents a
        lingering fleet/coverage tail, not a measured production split. These assumptions apply
        equally to all selected issues.
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        <KpiCard
          label="Without corrective action"
          value={fmtNumber(without)}
          hint="Claims over the six-month horizon"
        />
        <KpiCard
          label="With corrective action"
          value={fmtNumber(withAction)}
          hint={`${fmtNumber(postAction)} still expected from implementation onward within this horizon`}
        />
        <KpiCard
          label="Potential claims avoided"
          value={fmtNumber(without - withAction)}
          hint={`${fmtCurrency(trend.reduce((sum, p) => sum + p.cost - p.withActionCost, 0))} gross cost difference · action cost excluded`}
        />
      </div>
      <div
        className="mt-4 h-72 min-w-0"
        role="img"
        aria-label="Claims predicted with and without corrective action"
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trend} margin={{ top: 25, right: 20, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} />
            <YAxis width={45} tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} />
            <Tooltip
              content={({ active, payload, label }) =>
                active && payload?.length ? (
                  <div className="rounded border border-border bg-popover p-3 text-xs text-popover-foreground">
                    <p>{label}</p>
                    {payload.map((p) => (
                      <p key={String(p.dataKey)}>
                        {p.name}: {fmtNumber(Number(p.value))}
                      </p>
                    ))}
                  </div>
                ) : null
              }
            />
            <Legend />
            <ReferenceLine
              x={implementation}
              stroke="var(--warning)"
              strokeDasharray="4 4"
              label={{
                value: "CA implementation",
                position: "top",
                fill: "var(--warning)",
                fontSize: 11,
              }}
            />
            <Line
              name="Without action"
              dataKey="claims"
              stroke="var(--chart-2)"
              strokeDasharray="5 4"
              strokeWidth={2}
              isAnimationActive={false}
            />
            <Line
              name="With action · claims still coming"
              dataKey="remaining"
              stroke="var(--chart-4)"
              strokeWidth={2}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {!trend.some((p) => p.month === implementation) && (
        <p className="text-xs text-warning">
          Implementation is outside the displayed six-month period.
        </p>
      )}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[650px] text-left text-xs">
          <thead className="border-b border-border">
            <tr>
              {[
                "Month",
                "Without action",
                "Claims still coming with action",
                "Estimated cost with action",
              ].map((s) => (
                <th key={s} className="p-2 font-medium">
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trend.map((p) => (
              <tr key={p.month} className="border-b border-border/50">
                <td className="p-2">
                  {p.month}
                  {p.month === implementation ? " · CA implementation" : ""}
                </td>
                <td className="num p-2">{fmtNumber(p.claims)}</td>
                <td className="num p-2">{fmtNumber(p.remaining)}</td>
                <td className="num p-2">{fmtCurrency(p.withActionCost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        A production fix does not repair vehicles already in service. Validate coverage, build
        dates, units produced/in service, claim-age patterns and any field repair campaign before
        using this comparison for real decisions; no record, exposure, workflow or approval is
        changed.
      </p>
    </section>
  );
}
